import { DeleteOutlined, EditOutlined, MenuOutlined } from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from 'antd';
import type { FormListFieldData } from 'antd/es/form/FormList';
import React from 'react';
import { Link } from 'react-router-dom';

import { SListItem } from 'app/components/StaycationUI/List';
import type { RoomType } from 'app/components/commons/Package/General/Types';
import { PictureComponent } from 'app/components/commons/PictureComponent/PictureComponent';
import { useAppSelector } from 'app/redux/hooks';
import { hasBabyBed, maxRoomCapacity } from 'app/utils/room';
import { pluralize } from 'app/utils/strings';

import './RoomItem.scss';

export const RoomItem = ({
  field,
  room,
  mode,
  remove,
  dndId,
  disabled,
}: Props) => {
  const bedCategories = useAppSelector((state) => state.conf.bedCategories);

  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: dndId ?? field?.key ?? '', disabled: !!disabled });

  if (!field || !room) {
    return null;
  }

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const RoomItemTitle = () => {
    return (
      <div className="room-item-title">
        <div className="title-description">
          <div>{room?.title}</div>
          <div className="room-description">{room?.description}</div>
        </div>
        <Badge
          status={room.published ? 'success' : 'warning'}
          text={room.published ? 'Published' : 'Unpublished'}
        />
      </div>
    );
  };

  const renderAvatar = () => (
    <div
      className={`room-item__avatar ${
        room.avatar?.name.includes('emoji') ? 'room-item__avatar-emoji' : ''
      }`}
    >
      <PictureComponent {...room.avatar} width={56} />
    </div>
  );

  const renderEdit = () => (
    <div className="edit-link">
      <Link to={`/rooms/legacy/${room.id}`} target="_blank">
        <EditOutlined />
      </Link>
    </div>
  );

  const renderDelete = () => <DeleteOutlined onClick={remove} />;

  const renderDragHandler = () => (
    <MenuOutlined
      key="handle"
      className="room-item__drag-handle"
      style={{ cursor: 'grab' }}
      {...attributes}
      {...listeners}
    />
  );

  const renderCapacity = () => {
    return (
      <>
        {room.beds &&
          pluralize(
            maxRoomCapacity(room.beds, bedCategories),
            'guest',
            'guests'
          )}
        {room.beds && hasBabyBed(room.beds) && ' + 1 baby'}
      </>
    );
  };

  const renderActions = () => {
    if (mode === 'edit') {
      return [
        renderCapacity(),
        renderEdit(),
        renderDelete(),
        renderDragHandler(),
      ];
    }

    return [renderCapacity()];
  };

  return (
    <SListItem
      ref={setNodeRef}
      style={style}
      title={RoomItemTitle()}
      avatar={renderAvatar()}
      actions={renderActions()}
      className="room-item"
    />
  );
};

type Props = {
  field?: FormListFieldData;
  room?: RoomType;
  mode: string;
  remove: () => void;
  dndId?: number;
  disabled?: boolean;
};
